import { Purchases, ErrorCode } from '@revenuecat/purchases-js';

let isConfigured = false;

export const initializeRevenueCat = async (userId) => {
  if (isConfigured) {
    return Purchases.getSharedInstance();
  }

  const apiKey = import.meta.env.VITE_REVENUECAT_PUBLIC_API_KEY;

  if (!apiKey) {
    throw new Error('RevenueCat API key not configured');
  }

  await Purchases.configure(apiKey, userId);
  isConfigured = true;
  console.log('RevenueCat initialized for user:', userId);

  return Purchases.getSharedInstance();
};

export const getRevenueCat = () => {
  if (!isConfigured) {
    throw new Error('RevenueCat not initialized. Call initializeRevenueCat first.');
  }
  return Purchases.getSharedInstance();
};

export const resetRevenueCat = () => {
  isConfigured = false;
};

export const getOfferings = async () => {
  try {
    const rc = getRevenueCat();
    if (!rc) throw new Error('RevenueCat not initialized');

    const offerings = await rc.getOfferings();
    console.log('RevenueCat offerings response:', offerings);
    console.log('All offerings:', offerings.all);
    console.log('Current offering:', offerings.current);

    let offering = offerings.current;

    if (!offering && offerings.all && Object.keys(offerings.all).length > 0) {
      const firstOfferingKey = Object.keys(offerings.all)[0];
      offering = offerings.all[firstOfferingKey];
      console.log('No current offering set, using first available:', firstOfferingKey, offering);
    }

    console.log('Available packages:', offering?.availablePackages);

    return offering;
  } catch (error) {
    console.error('Failed to get offerings:', error);
    throw error;
  }
};

export const purchasePackage = async (pkg, { customerEmail } = {}) => {
  try {
    const rc = getRevenueCat();
    if (!rc) throw new Error('RevenueCat not initialized');

    const purchaseResult = await rc.purchase({
      rcPackage: pkg,
      ...(customerEmail ? { customerEmail } : {}),
    });

    return {
      success: true,
      customerInfo: purchaseResult.customerInfo,
      storeTransaction: purchaseResult.storeTransaction,
    };
  } catch (error) {
    console.error('Purchase failed:', error);

    if (error?.errorCode === ErrorCode.UserCancelledError) {
      return { success: false, cancelled: true, error: 'Purchase cancelled' };
    }

    return {
      success: false,
      error: error.message || 'Purchase failed',
    };
  }
};

const matchesProductPrefix = (productId, productPrefix) => {
  if (!productId || !productPrefix) return false;
  return productId === productPrefix || productId.startsWith(`${productPrefix}_v`);
};

export const findPackageByProductPrefix = async (productPrefix) => {
  const rc = getRevenueCat();
  if (!rc) throw new Error('RevenueCat not initialized');

  const offerings = await rc.getOfferings();
  const offeringList = [];

  if (offerings.current) {
    offeringList.push(offerings.current);
  }
  if (offerings.all) {
    offeringList.push(...Object.values(offerings.all));
  }

  for (const offering of offeringList) {
    for (const pkg of offering.availablePackages || []) {
      const productId =
        pkg.webBillingProduct?.identifier || pkg.rcBillingProduct?.identifier;
      if (matchesProductPrefix(productId, productPrefix)) {
        return pkg;
      }
    }
  }

  return null;
};

export const getTransactionIdFromPurchase = (purchaseResult, productPrefix) => {
  const storeId = purchaseResult?.storeTransaction?.storeTransactionId;
  if (storeId) {
    return storeId;
  }

  const transactions = purchaseResult?.customerInfo?.nonSubscriptionTransactions || [];
  const matching = transactions
    .filter((txn) => matchesProductPrefix(txn.productIdentifier, productPrefix))
    .sort((a, b) => new Date(b.purchaseDate) - new Date(a.purchaseDate));

  const latest = matching[0];
  if (!latest) {
    return null;
  }

  return latest.storeTransactionId || latest.transactionIdentifier || null;
};

export const purchaseTokenPackage = async (packageName) => {
  const pkg = await findPackageByProductPrefix(packageName);
  if (!pkg) {
    return {
      success: false,
      error: `Token package not available: ${packageName}`,
    };
  }

  const purchaseResult = await purchasePackage(pkg);
  if (!purchaseResult.success) {
    return purchaseResult;
  }

  const transactionId = getTransactionIdFromPurchase(purchaseResult, packageName);
  if (!transactionId) {
    return {
      success: false,
      error: 'Could not verify purchase transaction. Please contact support.',
    };
  }

  return {
    success: true,
    transactionId,
    customerInfo: purchaseResult.customerInfo,
  };
};

export const restorePurchases = async () => {
  try {
    const rc = getRevenueCat();
    if (!rc) throw new Error('RevenueCat not initialized');

    const customerInfo = await rc.restorePurchases();

    return {
      success: true,
      customerInfo,
    };
  } catch (error) {
    console.error('Restore failed:', error);
    return {
      success: false,
      error: error.message || 'Failed to restore purchases',
    };
  }
};

export const getCustomerInfo = async () => {
  try {
    const rc = getRevenueCat();
    if (!rc) throw new Error('RevenueCat not initialized');

    return await rc.getCustomerInfo();
  } catch (error) {
    console.error('Failed to get customer info:', error);
    return null;
  }
};

// The customer's active Web Billing (rc_billing) subscription for a plan, with the
// customer-portal link RevenueCat provides for it. Mobile and promotional
// subscriptions are ignored: they have no Web Billing portal.
export const getActiveWebSubscription = async (planId) => {
  const customerInfo = await getCustomerInfo();
  const subscriptions = Object.values(customerInfo?.subscriptionsByProductIdentifier || {});
  return subscriptions.find(
    (sub) =>
      sub.isActive &&
      sub.store === 'rc_billing' &&
      sub.managementURL &&
      (sub.productIdentifier || '').toLowerCase().includes(planId)
  ) || null;
};
